import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Vorgangs-Tracking (Phase 4, Feb 2026)
 *
 * Ein globaler Provider, der alle laufenden + jüngst-fertigen KI-Generierungen
 * im 4-Sekunden-Takt vom Backend abruft. Pages und Komponenten konsumieren ihn
 * über `useVorgaenge()`.
 *
 * Wichtige Verhaltensweisen:
 * - Erkennt Übergänge `generating → ready` und feuert einen Toast mit
 *   „Anzeigen"-Aktion (sonst würde der User die Fertigstellung verpassen,
 *   wenn er auf einer anderen Seite arbeitet).
 * - Erkennt Übergänge `generating → error` und feuert einen Error-Toast.
 * - Liefert `refreshTick` – ein Counter, der bei jedem Übergang inkrementiert.
 *   Pages können darauf reagieren (`useEffect` mit refreshTick in den deps),
 *   um z. B. die Course-Liste neu zu laden.
 * - `optimisticAdd(stub)` erlaubt dem Bibliothek-Submit, eine frische
 *   Generierung sofort sichtbar zu machen, ohne auf den nächsten Poll warten
 *   zu müssen.
 * - `cancel(id)` bricht eine laufende Generierung ab (Soft-Cancel auf Backend).
 *
 * Migrationspfad: sobald wir SSE oder WebSocket einführen, ersetzt der
 * Provider sein `setInterval`-Polling durch einen Event-Stream — Schnittstelle
 * für Konsumenten bleibt identisch.
 */

const POLL_INTERVAL_MS = 4000;
// Backoff: bei länger anhaltender Leere skalieren wir das Polling auf
// max. 12 s hoch, um bei vielen Tabs Last zu sparen. Sobald wieder ein
// Vorgang läuft, springen wir zurück auf 4 s.
const POLL_INTERVAL_EMPTY_MAX_MS = 12000;

const VorgaengeContext = createContext({
  jobs: [],
  refreshTick: 0,
  cancel: async () => {},
  optimisticAdd: () => {},
  triggerRefresh: () => {},
});

export function VorgaengeProvider({ children, inWizard = false }) {
  const [jobs, setJobs] = useState([]);
  const [refreshTick, setRefreshTick] = useState(0);
  const prevJobsRef = useRef([]);
  const navigate = useNavigate();
  // Toasts pro Job-ID nur einmal feuern (gegen Doppelfeuer durch Polling-Rennen).
  const notifiedRef = useRef(new Set());
  // Im Setup-Wizard ist „Anzeigen" wirkungslos (Overlay bleibt über der
  // Welt-Seite) → Action dort weglassen. Ref, damit der Polling-Effect nicht
  // neu startet und trotzdem den Live-Wert liest (22.09.2026).
  const inWizardRef = useRef(inWizard);
  inWizardRef.current = inWizard;

  const triggerRefresh = useCallback(() => {
    setRefreshTick((t) => t + 1);
  }, []);

  // Optimistische Anzeige direkt nach `POST /api/courses/generate`.
  const optimisticAdd = useCallback((stub) => {
    setJobs((prev) => {
      if (prev.some((j) => j.id === stub.id)) return prev;
      return [
        ...prev,
        {
          id: stub.id,
          title: stub.title || `Wird erstellt: ${stub.topic || ""}`,
          topic: stub.topic || "",
          status: "generating",
          started_at: new Date().toISOString(),
          finished_at: null,
          elapsed_seconds: 0,
          is_world: false,
          error_message: null,
        },
      ];
    });
  }, []);

  const cancel = useCallback(
    async (id) => {
      // Sofort optimistisch aus dem Tray entfernen – fühlt sich snappier an.
      setJobs((prev) => prev.filter((j) => j.id !== id));
      try {
        await api.deleteCourse(id);
        toast.success("Generierung abgebrochen.");
        triggerRefresh();
      } catch (e) {
        // Bei Fehler den Eintrag zurückholen wäre möglich – wir polish'en
        // das später, sobald wir SSE haben.
        toast.error(e?.response?.data?.detail || "Abbruch fehlgeschlagen.");
        triggerRefresh();
      }
    },
    [triggerRefresh]
  );

  useEffect(() => {
    let cancelledFlag = false;
    let timeoutId = null;
    let consecutiveEmpty = 0;

    const tick = async () => {
      try {
        const data = await api.listJobsActive();
        if (cancelledFlag) return;

        const prev = prevJobsRef.current;
        const prevById = new Map(prev.map((j) => [j.id, j]));
        let transitions = 0;

        for (const j of data) {
          const before = prevById.get(j.id);
          // Übergang generating → ready
          if (
            before?.status === "generating" &&
            j.status === "ready" &&
            !notifiedRef.current.has(`done:${j.id}`)
          ) {
            notifiedRef.current.add(`done:${j.id}`);
            transitions += 1;
            const isWorld = j.is_world;
            toast.success(
              `${"\u201E"}${j.title}${"\u201C"} ist fertig${isWorld ? " – bitte prüfen und freigeben." : "."}`,
              {
                action:
                  isWorld && !inWizardRef.current
                    ? {
                        label: "Anzeigen",
                        onClick: () => navigate(`/unternehmer/welt/${j.id}`),
                      }
                    : undefined,
                duration: 10000,
              }
            );
          }
          // Übergang generating → error
          if (
            before?.status === "generating" &&
            j.status === "error" &&
            !notifiedRef.current.has(`err:${j.id}`)
          ) {
            notifiedRef.current.add(`err:${j.id}`);
            transitions += 1;
            toast.error(
              j.error_message ||
                `Generierung ${"\u201E"}${j.topic || j.title}${"\u201C"} fehlgeschlagen.`,
              { duration: 10000 }
            );
          }
        }

        // Ghost-Card-Fix (23.08.2026): „Verschwunden-Regel". Wenn ein Job im
        // vorherigen Poll als `generating` sichtbar war und JETZT komplett aus
        // dem `/jobs/active`-Feed verschwunden ist (weder ready/error/generating
        // in `data`), hat der Client den Übergang wegen des Feed-Zeitfensters
        // (`JOB_VISIBLE_AFTER_FINISH_SECONDS` im Backend) verpasst. Wir werten
        // das als impliziten Terminierungs-Anlass und bumpen `refreshTick`,
        // damit die Course-Liste neu geladen wird und die `<PendingRow>` durch
        // die tatsächliche Row ersetzt wird. Kein Toast (Zustand ist unklar —
        // ready, error oder deleted; die neu geladene Liste zeigt Wahrheit).
        const dataIds = new Set(data.map((j) => j.id));
        for (const p of prev) {
          if (p.status === "generating" && !dataIds.has(p.id)) {
            transitions += 1;
          }
        }

        prevJobsRef.current = data;
        setJobs(data);
        if (transitions > 0) {
          setRefreshTick((t) => t + 1);
        }

        // Backoff-Steuerung: wenn nichts läuft, langsamer pollen.
        if (data.length === 0) {
          consecutiveEmpty += 1;
        } else {
          consecutiveEmpty = 0;
        }
      } catch {
        // Stiller Fehler – nächster Tick versucht es erneut.
      }

      if (cancelledFlag) return;
      // Erste 2 leere Polls noch im 4-s-Takt, danach hoch auf 8 s, dann 12 s.
      const nextDelay =
        consecutiveEmpty <= 1
          ? POLL_INTERVAL_MS
          : Math.min(
              POLL_INTERVAL_EMPTY_MAX_MS,
              POLL_INTERVAL_MS * (consecutiveEmpty - 1) * 2
            );
      timeoutId = setTimeout(tick, nextDelay);
    };
    tick();
    return () => {
      cancelledFlag = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [navigate]);

  return (
    <VorgaengeContext.Provider
      value={{ jobs, refreshTick, cancel, optimisticAdd, triggerRefresh }}
    >
      {children}
    </VorgaengeContext.Provider>
  );
}

export function useVorgaenge() {
  return useContext(VorgaengeContext);
}
